import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRightLeft, CheckSquare, Download, FolderPlus, ImageIcon, Minus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { isGuest } from "@/lib/guest";
import { useSpace } from "@/context/SpaceContext";
import { BookGrid } from "@/components/book/BookGrid";
import { MoveDialog } from "./MoveDialog";
import { EventDialog } from "./EventDialog";
import { Lightbox } from "./Lightbox";
import { SelectionDock } from "./SelectionDock";
import { AddToAlbumDialog } from "./AddToAlbumDialog";
import { InfoPanel } from "./InfoPanel";
import { usePhotoParam } from "@/hooks/usePhotoParam";

// Move an item whose date changed to its sorted spot (only when the list is date-sorted).
const reposition = (items, fresh, hasMore) => {
  const t = (i) => i.taken_at_utc || "";
  const rest = items.filter((i) => i.id !== fresh.id);
  const n = rest.length;
  const asc = n > 1 && t(rest[0]) < t(rest[n - 1]);
  const sorted = rest.every((x, k) => k === 0 || (asc ? t(rest[k - 1]) <= t(x) : t(rest[k - 1]) >= t(x)));
  if (!sorted) return { items: items.map((i) => (i.id === fresh.id ? fresh : i)), index: null };
  const before = (x) => (asc ? t(fresh) < t(x) : t(fresh) > t(x));
  let at = rest.findIndex(before);
  if (at === -1) {
    if (hasMore) return { items: rest, index: null }; // belongs in a page not loaded yet; it appears when you scroll there
    at = n;
  }
  return { items: [...rest.slice(0, at), fresh, ...rest.slice(at)], index: at };
};

const summarize = (verb, r) => `${verb} ${r.done}${r.skipped.length ? ` · ${r.skipped.length} skipped (${r.skipped[0].reason})` : ""}`;

export const LibraryView = ({ feed, space, timeline = true, lead, empty, album, toolbar, head, view = "book", total, onSelection }) => {
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  useEffect(() => { onSelection?.([...selected]); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
  const [localIdx, setLocalIdx] = useState(null);
  const url = usePhotoParam(feed);
  const openIdx = album ? url.index : localIdx; // inside albums the open photo lives in ?photo=
  const setOpenIdx = useCallback((v) => {
    if (!album) return setLocalIdx(v);
    const i = typeof v === "function" ? v(url.index) : v;
    if (i === null) url.close(); else url.setIndex(i);
  }, [album, url]);
  const [albumIds, setAlbumIds] = useState(null);
  const [moving, setMoving] = useState(null);
  const [eventIds, setEventIds] = useState(null);
  const { bump } = useSpace();
  const grid = useRef(null);
  const { items } = feed;
  const onUpdated = useCallback((fresh, byUser) => {
    const old = feed.items.find((i) => i.id === fresh.id);
    if (!byUser || !old || old.taken_at_utc === fresh.taken_at_utc) {
      feed.setItems((prev) => prev.map((i) => (i.id === fresh.id ? fresh : i)));
      return;
    }
    const moved = reposition(feed.items, fresh, feed.hasMore);
    feed.setItems(moved.items);
    setOpenIdx((idx) => (idx === null ? idx : Math.max(0, Math.min(moved.index ?? idx, moved.items.length - 1))));
  }, [feed.items, feed.setItems, feed.hasMore, setOpenIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  // keep the grid where it was; only scroll if the photo last viewed is off-screen
  const closeLightbox = useCallback(() => {
    const last = items[openIdx];
    setOpenIdx(null);
    if (last) requestAnimationFrame(() => grid.current?.scrollToItem(last.id));
  }, [items, openIdx, setOpenIdx]);

  const toggle = useCallback((id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }), []);
  const open = useCallback((id) => (album ? url.open(id) : setLocalIdx(items.findIndex((i) => i.id === id))), [album, url, items]);
  const clear = () => { setSelected(new Set()); setSelecting(false); };

  const bulk = async (ids, action) => {
    if (action === "delete" && !window.confirm(`Delete ${ids.length} item(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/items/bulk", { ids, action });
      const skipped = new Set(data.skipped.map((s) => s.id));
      if (action !== "copy") feed.removeIds(ids.filter((i) => !skipped.has(i)));
      toast(summarize({ delete: "Deleted", move: "Moved", copy: "Copied" }[action], data));
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  };

  const onDock = async (key) => {
    const ids = [...selected];
    if (key === "album") return setAlbumIds(ids);
    if (key === "event") return setEventIds(ids);
    if (key === "download") {
      if (ids.length > 20) return toast.error("Select up to 20 photos to download at once, or use Download all (ZIP) on an album");
      items.filter((i) => selected.has(i.id) && i.download_url).forEach((it, n) => setTimeout(() => {
        const a = document.createElement("a");
        a.href = mediaUrl(it.download_url);
        a.download = "";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }, n * 400));
      return null;
    }
    if (key === "move" || key === "copy") return setMoving({ action: key, items: items.filter((i) => selected.has(i.id)) });
    if (key === "remove") {
      await album.onRemove(ids);
      feed.removeIds(ids);
    } else if (!(await bulk(ids, key))) return;
    clear();
  };

  const closeAfter = (fn) => async (it) => {
    const res = await fn(it);
    if (res !== null) setOpenIdx((i) => (items.length <= 1 ? null : Math.min(i, items.length - 2)));
  };

  const readOnly = isGuest();
  const actions = readOnly ? [
    { key: "download", label: "Download original", icon: <Download size={18} />, href: (it) => it.download_url },
  ] : [
    { key: "download", label: "Download original", icon: <Download size={18} />, href: (it) => it.download_url },
    { key: "album", label: "Add to album", icon: <FolderPlus size={18} />, onClick: (it) => setAlbumIds([it.id]) },
    { key: "move", label: "Move…", icon: <ArrowRightLeft size={18} />, show: (it) => it.can_delete, onClick: (it) => setMoving({ action: "move", items: [it] }) },
    ...(album?.canRemove ? [
      { key: "cover", label: "Set as album cover", icon: <ImageIcon size={18} />, show: () => album.canEdit, onClick: (it) => album.onCover(it.id) },
      { key: "remove", label: "Remove from album", icon: <Minus size={18} />, onClick: closeAfter(async (it) => { await album.onRemove([it.id]); feed.removeIds([it.id]); }) },
    ] : []),
    { key: "delete", label: "Delete", icon: <Trash2 size={18} />, show: (it) => it.can_delete, onClick: closeAfter((it) => bulk([it.id], "delete")) },
  ];

  const tools = (
    <>
      {toolbar}
      {!readOnly && <button className={`tool-btn ${selecting ? "on" : ""}`} onClick={() => (selecting ? clear() : setSelecting(true))} data-testid="select-mode-button">
        <CheckSquare size={16} /> <span>{selecting ? "Done" : "Select"}</span>
      </button>}
    </>
  );
  return (
    <>
      {head ? head(tools) : <div className="view-toolbar">{tools}</div>}
      <BookGrid ref={grid} items={items} timeline={timeline} lead={lead} hasMore={feed.hasMore} loading={feed.loading} onLoadMore={feed.loadMore}
        empty={empty} selecting={selecting} selected={selected} onOpen={open} onToggle={toggle} mode={view} />
      <SelectionDock count={selected.size} onAction={onDock} onClear={clear} onSelectAll={() => setSelected(new Set(items.map((i) => i.id)))} album={album} />
      <EventDialog ids={eventIds} onClose={() => setEventIds(null)} onDone={() => { clear(); bump(); }} />
      <MoveDialog request={moving} onClose={() => setMoving(null)} onDone={() => { clear(); bump(); }} />
      {openIdx !== null && openIdx >= 0 && (
        <Lightbox items={items} index={openIdx} onIndex={setOpenIdx} onClose={closeLightbox} actions={actions} onNearEnd={feed.loadMore}
          total={total} onItemChange={(d) => d && onUpdated(d, true)} panel={(it) => <InfoPanel item={it} onUpdated={onUpdated} />} />
      )}
      <AddToAlbumDialog ids={albumIds} space={space} onClose={() => setAlbumIds(null)} onDone={() => {
        if (album?.unsorted) feed.removeIds(albumIds);  // filed items leave Unsorted
        if (selecting) clear();
      }} />
    </>
  );
};
